import { Button, Drawer } from '@toggle/design-system';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ExpandableDrawer } from '~/components/expandable-drawer/ExpandableDrawer';
import { useNavigateToThemeOverview } from '~/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useThemeCompanies } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useTheme } from '~/react-query-hooks/themes/useThemeOverview';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '~/stores/use-flags/useFlags';
import { DownloadButton } from '~/views/stream/components/download-button/DownloadButton';
import { ScheduleDemoBanner } from '~/views/stream/components/schedule-demo-banner/ScheduleDemoBanner';
import { handleDownloadDrawer } from '~/views/stream/utils/handleDownloadDrawer';

import { ThemePerfBandChart } from './components/ThemePerfBandChart';
import { MarketCatalystWidget } from './market-catalyst-widget/MarketCatalystWidget';
import styles from './MarketCatalystDrawer.module.scss';
import { useMarketCatalystDrawer } from './useMarketCatalystDrawer';

const MARKET_CATALYST_DRAWER_WIDTH = 788;

export const MarketCatalystDrawer = () => {
  const { t } = useTranslation(['insights']);
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { trackCatalystDrawerOpen, trackThemeOpen } = usePosthogEvents();
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );

  const [expandCitations, setExpandCitations] = useState(false);

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const { closeDrawer, isOpen, themeId, drawerType, insightId, utmSource } =
    useMarketCatalystDrawer();

  const { data: themeData } = useTheme({
    themeId: themeId ?? '',
  });

  const { data: themeCompanies } = useThemeCompanies({
    themeId: themeId ?? '',
    limit: 10,
    enabled: !!themeId,
  });

  useEffect(() => {
    if (isOpen && utmSource === POSTHOG_TRACKING_SOURCES.EMAIL) {
      trackCatalystDrawerOpen(utmSource, 'market');
    }
  }, [isOpen, utmSource]);

  const handleHeaderButtonClick = () => {
    if (themeId) {
      trackThemeOpen(POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER);
      navigateToTheme({ themeId });
    }

    return null;
  };

  const handleDownload = () => {
    if (contentWrapperRef.current) {
      handleDownloadDrawer({
        contentToPrint: contentWrapperRef.current,
        entityTag: themeId,
        expandAccordions: setExpandCitations,
      });
    }
  };

  if (!isOpen) {
    return null;
  }

  const headerButtons = (
    <div className={styles.headerButtons}>
      {drawerType === 'thematic' && (
        <Button
          variant="empty"
          label={t('insights:catalyst.viewOnTheme')}
          iconName="Expand"
          size="xsmall"
          onClick={handleHeaderButtonClick}
        />
      )}
      <DownloadButton onClick={handleDownload} />
    </div>
  );

  const getContent = () => (
    <div className={styles.contentWrapper} ref={contentWrapperRef}>
      <ErrorBoundaryWrapper>
        {!!insightId && (
          <MarketCatalystWidget
            insightId={insightId}
            expandCitations={expandCitations}
          />
        )}
      </ErrorBoundaryWrapper>
      <ScheduleDemoBanner />
    </div>
  );

  const entityTags = (themeCompanies ?? []).map(item => item.company);

  const fullViewContent = themeId && entityTags.length > 0 && (
    <ErrorBoundaryWrapper>
      <ThemePerfBandChart
        entityTags={entityTags}
        showHorizonSelector={false}
        themeId={themeId}
      />
    </ErrorBoundaryWrapper>

    //TODO: add event feed widget here
  );

  if (isUniversalDrawerEnabled && drawerType === 'thematic') {
    return (
      <ExpandableDrawer
        isOpen={isOpen}
        handleCloseClick={closeDrawer}
        headerButton={headerButtons}
        defaultWidth={MARKET_CATALYST_DRAWER_WIDTH}
        label={t('insights:viewAllSection.whatElseIsMoving', {
          name: themeData && themeData.name,
        })}
        fullViewContent={fullViewContent}
      >
        {getContent()}
      </ExpandableDrawer>
    );
  }

  return (
    <Drawer
      className={styles.styledDrawer}
      data-testid="market-catalyst-drawer"
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={headerButtons}
    >
      {getContent()}
    </Drawer>
  );
};
